import ClockIcon from 'components/CopilotControl/icons/ClockIcon'
import NegativeFaceIcon from 'components/CopilotControl/icons/NegativeFaceIcon'
import NeutralFaceIcon from 'components/CopilotControl/icons/NeutralFaceIcon'
import PositiveFaceIcon from 'components/CopilotControl/icons/PositiveFaceIcon'
import { CopilotEngagementCategories, CopilotEngagementTypes } from 'consts/copilotControlConsts'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export default ({ engagementType, values = {} }) => {
  const { t } = useTranslation()

  const iconTypes = useMemo(
    () => ({
      [CopilotEngagementTypes.PatientsSentiment]: {
        positive: PositiveFaceIcon,
        neutral: NeutralFaceIcon,
        negative: NegativeFaceIcon
      },
      [CopilotEngagementTypes.ScanningSchedule]: {
        positive: ClockIcon,
        neutral: ClockIcon,
        negative: ClockIcon
      }
    }),
    []
  )

  const summaryTextOptions = useMemo(
    () => ({
      [CopilotEngagementTypes.PatientsSentiment]: {
        negative: t(`pages.copilotControl.widgets.${CopilotEngagementTypes.PatientsSentiment}.negativeSummary`),
        neutral: t(`pages.copilotControl.widgets.${CopilotEngagementTypes.PatientsSentiment}.neutralSummary`),
        positive: t(`pages.copilotControl.widgets.${CopilotEngagementTypes.PatientsSentiment}.positiveSummary`)
      },
      [CopilotEngagementTypes.ScanningSchedule]: {
        negative: t(`pages.copilotControl.widgets.${CopilotEngagementTypes.ScanningSchedule}.negativeSummary`),
        neutral: t(`pages.copilotControl.widgets.${CopilotEngagementTypes.ScanningSchedule}.neutralSummary`),
        positive: t(`pages.copilotControl.widgets.${CopilotEngagementTypes.ScanningSchedule}.positiveSummary`)
      }
    }),
    [t]
  )

  const scoreOptions = useMemo(
    () => ({
      [CopilotEngagementCategories.Negative]: {
        key: CopilotEngagementCategories.Negative,
        fillColor: '#FFEEEE',
        strokeColor: '#EB5757',
        icon: iconTypes[engagementType].negative,
        value: values.negative,
        label: t(`pages.copilotControl.widgets.${engagementType}.negativeLabel`),
        description: t(`pages.copilotControl.widgets.${engagementType}.negativeDescription`)
      },
      [CopilotEngagementCategories.Neutral]: {
        key: CopilotEngagementCategories.Neutral,
        fillColor: '#FFF5E1',
        strokeColor: '#FFBD39',
        icon: iconTypes[engagementType].neutral,
        value: values.neutral,
        label: t(`pages.copilotControl.widgets.${engagementType}.neutralLabel`),
        description: t(`pages.copilotControl.widgets.${engagementType}.neutralDescription`)
      },
      [CopilotEngagementCategories.Positive]: {
        key: CopilotEngagementCategories.Positive,
        fillColor: '#E9FDF3',
        strokeColor: '#26C36F',
        icon: iconTypes[engagementType].positive,
        value: values.positive,
        label: t(`pages.copilotControl.widgets.${engagementType}.positiveLabel`),
        description: t(`pages.copilotControl.widgets.${engagementType}.positiveDescription`)
      }
    }),
    [engagementType, t, values, iconTypes]
  )

  const negativeWeight = 1.5
  const neutralWeight = 3
  const positiveWeight = 4.5

  const totalCount = useMemo(() => (values.negative || 0) + (values.neutral || 0) + (values.positive || 0), [values])

  const averageScore = useMemo(
    () =>
      totalCount === 0
        ? 0
        : ((values.negative || 0) * negativeWeight +
            (values.neutral || 0) * neutralWeight +
            (values.positive || 0) * positiveWeight) /
          totalCount,
    [totalCount, values, negativeWeight, neutralWeight, positiveWeight]
  )

  const averageScoreType = useMemo(
    () =>
      averageScore <= 2.5
        ? CopilotEngagementCategories.Negative
        : averageScore <= 3.5
        ? CopilotEngagementCategories.Neutral
        : CopilotEngagementCategories.Positive,
    [averageScore]
  )

  return {
    scoreOptions,
    averageScore,
    averageScoreType,
    summaryTextOptions
  }
}
